import {
  SLOT_H, TOTAL_S, MIN_DUR, START_H,
  DAYS_SHORT, COLORS, GCAL_COLORS, DAY_PILL_LABELS,
  slotToHHMM, genId, dateISO, esc, weekKey, toast,
} from './config.js';
import { state, getWeekBlocks, setWeekBlocks, findBlock, patchBlock, removeBlock } from './state.js';
import {
  saveBlockAPI, updateBlockAPI, deleteBlockAPI, deleteBlockFromCalendar,
  saveTemplateAPI, deleteTemplateAPI, addExceptionAPI,
  loadGoogleCalendarsAPI, loadSelectedCalendarsAPI, saveSelectedCalendarsAPI,
  loadCalendarEventsAPI, loadBlockTypesAPI, apiFetch,
  pushBlockToCalendar, updateBlockInCalendar, updateTaskAPI,
} from './api.js';

// ══════════════════════════════════════════════════════════
// TEMPLATES
// ══════════════════════════════════════════════════════════
export function saveTemplate(tpl) {
  const idx = state.templates.findIndex(t => t.id === tpl.id);
  const isNew = idx === -1;
  if (isNew) {
    state.templates.push(tpl);
  } else {
    state.templates[idx] = tpl;
  }
  saveTemplateAPI(tpl, isNew).catch(() => {});
}

export function deleteTemplate(id) {
  deleteTemplateAPI(id).catch(() => {});
  state.templates = state.templates.filter(t => t.id !== id);
  for (const k in state.db) {
    state.db[k].filter(b => b.templateId === id).forEach(b => {
      if (b.calendarEventId) deleteBlockFromCalendar(b.calendarEventId).catch(() => {});
      deleteBlockAPI(b.id).catch(() => {});
    });
    state.db[k] = state.db[k].filter(b => b.templateId !== id);
  }
}

export function getTemplateById(id) {
  return state.templates.find(t => t.id === id) || null;
}

// ══════════════════════════════════════════════════════════
// GENERATE WEEK FROM TEMPLATES
// ══════════════════════════════════════════════════════════
export function generateWeekFromTemplates(wKey) {
  const mondayDate = new Date(wKey + 'T00:00:00');
  if (!state.db[wKey]) state.db[wKey] = [];

  const newBlocks = [];

  for (const tpl of state.templates) {
    for (const dayIdx of tpl.days) {
      const isoDate = dateISO(mondayDate, dayIdx);
      if (state.exceptions[`${tpl.id}|${isoDate}`]) continue;
      if (state.db[wKey].some(b => b.templateId === tpl.id && b.day === dayIdx)) continue;

      const block = {
        id:         genId(),
        title:      tpl.title,
        day:        dayIdx,
        startSlot:  tpl.startSlot,
        dur:        tpl.dur,
        color:      tpl.color,
        notes:      tpl.notes || '',
        tipusId:    tpl.tipusId || null,
        energia:    tpl.energia || '',
        templateId: tpl.id,
        done:       false,
        doneAt:     null,
        calendarEventId: null,
        taskIds:    [],
      };
      state.db[wKey].push(block);
      newBlocks.push(block);
    }
  }

  if (newBlocks.length > 0) {
    apiFetch('/api/blocks/batch', {
      method: 'POST',
      body: JSON.stringify({ blocks: newBlocks.map(b => ({ ...b, weekKey: wKey })) }),
    }).catch(() => {});
  }
}

// ══════════════════════════════════════════════════════════
// MODAL — TIME SELECTS & FILL HELPERS
// ══════════════════════════════════════════════════════════
export function fillTimeSelects() {
  const fStart = document.getElementById('f-start');
  const fEnd   = document.getElementById('f-end');
  const startOpts = [];
  const endOpts   = [];
  for (let s = 0; s < TOTAL_S; s++) {
    startOpts.push(`<option value="${s}">${slotToHHMM(s)}</option>`);
  }
  for (let s = 1; s <= TOTAL_S; s++) {
    endOpts.push(`<option value="${s}">${slotToHHMM(s)}</option>`);
  }
  fStart.innerHTML = startOpts.join('');
  fEnd.innerHTML   = endOpts.join('');
}

function fillTipusButtons(activeId) {
  const grid = document.getElementById('tipus-grid');
  grid.innerHTML = '';
  state.blockTypes.forEach(t => {
    const colorId = COLORS.find(c => c.id === t.color) ? t.color : 'sky';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `tipus-btn b-${colorId}${t.id === activeId ? ' active' : ''}`;
    btn.innerHTML = `<span class="tipus-btn-emoji">${t.emoji || ''}</span><span class="tipus-label">${esc(t.label)}</span>`;
    btn.dataset.id = t.id;
    btn.addEventListener('click', () => {
      state.pickedTipus = t.id;
      state.pickedColor = t.color;
      document.querySelectorAll('.tipus-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const dot = document.getElementById('modal-color-dot');
      if (dot) dot.style.background = `var(--${colorId}-fg)`;
    });
    grid.appendChild(btn);
  });
  const dot = document.getElementById('modal-color-dot');
  const activeDef = state.blockTypes.find(t => t.id === activeId);
  if (dot && activeDef) {
    const colorId = COLORS.find(c => c.id === activeDef.color) ? activeDef.color : 'sky';
    dot.style.background = `var(--${colorId}-fg)`;
  }
}

function fillDayPills(activeDays) {
  const container = document.getElementById('day-pills');
  container.innerHTML = '';
  DAY_PILL_LABELS.forEach((label, i) => {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'day-pill' + (activeDays.has(i) ? ' active' : '');
    pill.textContent = label;
    pill.dataset.day = i;
    pill.addEventListener('click', () => {
      if (state.pickedDays.has(i)) {
        state.pickedDays.delete(i);
        pill.classList.remove('active');
      } else {
        state.pickedDays.add(i);
        pill.classList.add('active');
      }
    });
    container.appendChild(pill);
  });
}

function setRecurToggle(enabled) {
  state.recurEnabled = enabled;
  document.getElementById('f-recur').checked = enabled;
  document.getElementById('day-pills-section').style.display = enabled ? 'block' : 'none';
}

// ══════════════════════════════════════════════════════════
// MODAL — OPEN / CLOSE / SAVE
// ══════════════════════════════════════════════════════════
export function openCreateModal(day, rawSlot, rawEndSlot, prefill = null) {
  state.editingId     = null;
  state.pickedColor   = COLORS[0].id;
  state.pickedTipus   = '';
  state.pickedDays    = new Set([day]);
  state.pendingTaskId = prefill?.taskId || null;

  document.getElementById('modal-title').textContent = 'Nou bloc';
  document.getElementById('modal-time-hint').textContent = '';
  document.getElementById('f-day').value = day;
  document.getElementById('btn-del-block').style.display = 'none';
  document.getElementById('btn-dup-block').style.display = 'none';

  fillTimeSelects();
  const slot    = Math.max(0, Math.min(TOTAL_S - MIN_DUR, rawSlot));
  const endSlot = rawEndSlot != null
    ? Math.max(slot + MIN_DUR, Math.min(TOTAL_S, rawEndSlot))
    : Math.min(slot + 4, TOTAL_S);
  document.getElementById('f-start').value = slot;
  document.getElementById('f-end').value   = endSlot;

  if (prefill?.tipusId) {
    state.pickedTipus = prefill.tipusId;
    const tipusDef = state.blockTypes.find(t => t.id === prefill.tipusId);
    if (tipusDef) state.pickedColor = tipusDef.color;
  }

  fillTipusButtons(state.pickedTipus);
  fillDayPills(state.pickedDays);
  setRecurToggle(false);

  _openOverlay();
}

export function openCreateModalDefault() {
  const today = new Date();
  const jsDay = today.getDay();
  const day = jsDay === 0 ? 6 : jsDay - 1;
  const min = today.getHours() * 60 + today.getMinutes() - START_H * 60;
  const slot = Math.max(0, Math.min(TOTAL_S - 4, Math.floor(min / 15)));
  openCreateModal(day, slot);
}

export function openEditModal(id) {
  const b = findBlock(id);
  if (!b) return;

  state.editingId     = id;
  state.pickedColor   = b.color;
  state.pickedTipus   = b.tipusId || b.tipus || '';
  state.pendingTaskId = null;

  const tpl = b.templateId ? getTemplateById(b.templateId) : null;
  state.pickedDays = tpl ? new Set(tpl.days) : new Set([b.day]);

  document.getElementById('modal-title').textContent = 'Editar bloc';
  const startHint = slotToHHMM(b.startSlot);
  const endHint   = slotToHHMM(b.startSlot + b.dur);
  document.getElementById('modal-time-hint').textContent = `· ${DAYS_SHORT[b.day]}, ${startHint} – ${endHint}`;
  document.getElementById('f-day').value = b.day;
  document.getElementById('btn-del-block').style.display = 'block';
  document.getElementById('btn-dup-block').style.display = 'block';

  fillTimeSelects();
  document.getElementById('f-start').value = b.startSlot;
  document.getElementById('f-end').value   = b.startSlot + b.dur;

  fillTipusButtons(state.pickedTipus);
  fillDayPills(state.pickedDays);
  setRecurToggle(!!b.templateId);

  _openOverlay();
}

function _openOverlay() {
  document.getElementById('modal-overlay').classList.add('open');
}

export function closeModal() {
  document.getElementById('modal-overlay').classList.remove('open');
  state.editingId = null;
}

export function saveModal() {
  if (!state.pickedTipus) { toast('Selecciona un tipus de bloc'); return; }
  const tipusDef = state.blockTypes.find(t => t.id === state.pickedTipus);
  if (!tipusDef) { toast('Tipus de bloc no vàlid'); return; }

  const day   = parseInt(document.getElementById('f-day').value);
  const start = parseInt(document.getElementById('f-start').value);
  const end   = parseInt(document.getElementById('f-end').value);

  if (isNaN(day)   || day < 0   || day > 6)        { toast('Dia no vàlid'); return; }
  if (isNaN(start) || start < 0 || start >= TOTAL_S) { toast('Hora inici no vàlida'); return; }
  if (isNaN(end)   || end < 1   || end > TOTAL_S)   { toast('Hora fi no vàlida'); return; }
  if (end <= start)                                   { toast("La fi ha de ser després de l'inici"); return; }

  const dur = Math.max(MIN_DUR, end - start);

  const blockData = {
    title: tipusDef.label, day, startSlot: start, dur,
    color: tipusDef.color, notes: '',
    tipusId: tipusDef.id, energia: '',
  };

  if (state.recurEnabled) {
    const days = Array.from(state.pickedDays).sort();
    if (days.length === 0) { toast('Selecciona almenys un dia'); return; }
    doSaveRecurring(blockData, days);
  } else {
    doSaveManual(blockData);
  }
}

function doSaveManual(blockData) {
  if (state.editingId) {
    const existing = findBlock(state.editingId);
    if (existing && existing.templateId) {
      closeModal();
      openChoiceModal(
        'Editar bloc recurrent',
        'Vols canviar només aquest dia o tots els futurs?',
        [
          {
            label: 'Només avui',
            action: () => {
              patchBlock(state.editingId, { ...blockData, templateId: null });
              rerenderBlockExternal(state.editingId);
              updateBlockAPI(state.editingId, { ...blockData, templateId: null }).catch(() => {});
              const _decoupledBlock = findBlock(state.editingId);
              if (_decoupledBlock?.calendarEventId) {
                updateBlockInCalendar(_decoupledBlock.calendarEventId, state.editingId).catch(() => {});
              }
              toast('Bloc actualitzat (només avui)');
            },
            danger: false,
          },
          {
            label: 'Tots els futurs',
            action: () => {
              const tpl = getTemplateById(existing.templateId);
              if (tpl) {
                const updatedTpl = { ...tpl, title: blockData.title, startSlot: blockData.startSlot, dur: blockData.dur, color: blockData.color, notes: blockData.notes, tipusId: blockData.tipusId || null, energia: blockData.energia };
                saveTemplate(updatedTpl);
                for (const k in state.db) {
                  state.db[k] = state.db[k].map(b => {
                    if (b.templateId === tpl.id) {
                      const updated = { ...b, ...blockData, templateId: tpl.id, day: b.day, id: b.id };
                      updateBlockAPI(b.id, updated).catch(() => {});
                      if (b.calendarEventId) updateBlockInCalendar(b.calendarEventId, b.id).catch(() => {});
                      return updated;
                    }
                    return b;
                  });
                }
                renderAllBlocksExternal();
                toast('Plantilla i blocs actualitzats');
              }
            },
            danger: false,
          },
        ]
      );
      return;
    }
    const _editId = state.editingId;
    patchBlock(_editId, { ...blockData, templateId: null });
    rerenderBlockExternal(_editId);
    const _calId = findBlock(_editId)?.calendarEventId;
    updateBlockAPI(_editId, { ...blockData, templateId: null }).then(() => {
      if (_calId) updateBlockInCalendar(_calId, _editId).catch(() => {});
    }).catch(() => {});
    toast('Bloc actualitzat');
  } else {
    const block = { id: genId(), ...blockData, templateId: null, taskIds: state.pendingTaskId ? [state.pendingTaskId] : [], done: false, doneAt: null, calendarEventId: null };
    const tid = state.pendingTaskId;
    state.pendingTaskId = null;
    const arr = getWeekBlocks();
    arr.push(block);
    setWeekBlocks(arr);
    renderBlockExternal(block);
    const revertBlock = () => {
      removeBlock(block.id);
      document.querySelector(`.block[data-id="${block.id}"]`)?.remove();
      toast('Error en guardar el bloc. Torna-ho a provar.');
    };
    saveBlockAPI(block).then(async saved => {
      if (!saved?.id) { revertBlock(); return; }
      const result = await pushBlockToCalendar(saved.id).catch(() => null);
      if (result?.calendarEventId) {
        patchBlock(block.id, { calendarEventId: result.calendarEventId });
      }
      if (tid) {
        updateTaskAPI(tid, { block_id: saved.id }).then(() => renderSidebarExternal()).catch(() => {});
      }
    }).catch(revertBlock);
    toast('Bloc creat');
  }
  closeModal();
}

function doSaveRecurring(blockData, days) {
  if (state.editingId) {
    const existing = findBlock(state.editingId);
    if (existing && existing.templateId) {
      closeModal();
      openChoiceModal(
        'Editar bloc recurrent',
        'Vols canviar només aquest dia o tots els futurs?',
        [
          {
            label: 'Només avui',
            action: () => {
              const _id = state.editingId;
              patchBlock(_id, { ...blockData, templateId: null });
              rerenderBlockExternal(_id);
              updateBlockAPI(_id, { ...blockData, templateId: null }).catch(() => {});
              const _recurBlock = findBlock(_id);
              if (_recurBlock?.calendarEventId) {
                updateBlockInCalendar(_recurBlock.calendarEventId, _id).catch(() => {});
              }
              toast('Bloc actualitzat (només avui)');
            },
            danger: false,
          },
          {
            label: 'Tots els futurs',
            action: () => {
              const tpl = getTemplateById(existing.templateId);
              if (tpl) {
                const updatedTpl = { ...tpl, ...blockData, tipusId: blockData.tipusId || null, days, id: tpl.id };
                saveTemplate(updatedTpl);
                for (const k in state.db) {
                  state.db[k].filter(b => b.templateId === tpl.id && !days.includes(b.day))
                             .forEach(b => {
                               if (b.calendarEventId) deleteBlockFromCalendar(b.calendarEventId).catch(() => {});
                               deleteBlockAPI(b.id).catch(() => {});
                             });
                  state.db[k] = state.db[k].filter(b => !(b.templateId === tpl.id && !days.includes(b.day)));
                  state.db[k] = state.db[k].map(b => {
                    if (b.templateId === tpl.id) {
                      const updated = { ...b, ...blockData, tipusId: blockData.tipusId || null, templateId: tpl.id, day: b.day, id: b.id };
                      updateBlockAPI(b.id, updated).catch(() => {});
                      if (b.calendarEventId) updateBlockInCalendar(b.calendarEventId, b.id).catch(() => {});
                      return updated;
                    }
                    return b;
                  });
                }
                generateWeekFromTemplates(weekKey(state.currentMonday));
                renderAllBlocksExternal();
                toast('Plantilla recurrent actualitzada');
              }
            },
            danger: false,
          },
        ]
      );
      return;
    }
  }

  const tplId = genId();
  const tpl = {
    id: tplId, title: blockData.title, days,
    startSlot: blockData.startSlot, dur: blockData.dur,
    color: blockData.color, notes: blockData.notes,
    tipusId: blockData.tipusId || null, energia: blockData.energia,
  };
  saveTemplate(tpl);

  generateWeekFromTemplates(weekKey(state.currentMonday));
  renderAllBlocksExternal();
  toast('Bloc recurrent creat');

  closeModal();
}

export function duplicateEditingBlock() {
  if (!state.editingId) return;
  const orig = findBlock(state.editingId);
  if (!orig) return;

  const newStart = Math.min(orig.startSlot + orig.dur, TOTAL_S - orig.dur);
  const copy = { ...orig, id: genId(), startSlot: newStart, templateId: null, calendarEventId: null };

  const arr = getWeekBlocks();
  arr.push(copy);
  setWeekBlocks(arr);
  renderBlockExternal(copy);
  updateWeekSummaryExternal();
  saveBlockAPI(copy).catch(() => {});
  closeModal();
  toast('Bloc duplicat');
}

export function deleteEditingBlock() {
  if (!state.editingId) return;
  const b = findBlock(state.editingId);
  closeModal();
  if (b) handleDeleteBlockExternal(b.id);
}

// ══════════════════════════════════════════════════════════
// CHOICE MODAL
// ══════════════════════════════════════════════════════════
export function openChoiceModal(title, subtitle, options) {
  document.getElementById('choice-title').textContent = title;
  document.getElementById('choice-subtitle').textContent = subtitle;

  const optContainer = document.getElementById('choice-options');
  optContainer.innerHTML = '';

  options.forEach(opt => {
    const btn = document.createElement('button');
    btn.className = 'choice-opt' + (opt.danger ? ' danger' : '');
    btn.textContent = opt.label;
    btn.addEventListener('click', () => {
      closeChoiceModal();
      opt.action();
    });
    optContainer.appendChild(btn);
  });

  document.getElementById('choice-overlay').classList.add('open');
}

export function closeChoiceModal() {
  document.getElementById('choice-overlay').classList.remove('open');
}

// ══════════════════════════════════════════════════════════
// SETTINGS — Block type management
// ══════════════════════════════════════════════════════════
export async function renderCalendarSettings() {
  const container = document.getElementById('calendar-list-container');
  if (!container) return;
  container.innerHTML = '<div style="color:var(--text-3);font-size:13px;">Carregant...</div>';

  await Promise.all([loadGoogleCalendarsAPI(), loadSelectedCalendarsAPI()]);

  if (state.allGoogleCalendars.length === 0) {
    container.innerHTML = '<div style="color:var(--text-3);font-size:13px;">No s\'han pogut carregar els calendaris.</div>';
    return;
  }

  container.innerHTML = state.allGoogleCalendars.map(cal => {
    const isSelected = state.selectedCalendarIds.includes(cal.id);
    return `<div style="display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid var(--border);" data-cal-id="${esc(cal.id)}">
      <span style="width:12px;height:12px;border-radius:50%;flex-shrink:0;background:${esc(cal.backgroundColor)};border:1px solid rgba(0,0,0,0.1);"></span>
      <span style="flex:1;font-size:13px;color:var(--text);">${esc(cal.summary)}${cal.primary ? ' <span style="font-size:10px;color:var(--text-3);">(principal)</span>' : ''}</span>
      <label style="cursor:pointer;display:flex;align-items:center;gap:0;">
        <input type="checkbox" class="cal-toggle" data-cal-id="${esc(cal.id)}" ${isSelected ? 'checked' : ''} style="cursor:pointer;width:16px;height:16px;">
      </label>
    </div>`;
  }).join('');

  container.querySelectorAll('.cal-toggle').forEach(cb => {
    cb.addEventListener('change', async () => {
      const calId = cb.dataset.calId;
      if (cb.checked) {
        if (!state.selectedCalendarIds.includes(calId)) state.selectedCalendarIds = [...state.selectedCalendarIds, calId];
      } else {
        state.selectedCalendarIds = state.selectedCalendarIds.filter(id => id !== calId);
        if (state.selectedCalendarIds.length === 0) {
          state.selectedCalendarIds = [calId];
          cb.checked = true;
          toast('Has de tenir almenys un calendari actiu');
          return;
        }
      }
      await saveSelectedCalendarsAPI(state.selectedCalendarIds);
      const wk = weekKey(state.currentMonday);
      await loadCalendarEventsAPI(wk);
      renderCalendarEventsExternal();
      toast('Calendaris actualitzats');
    });
  });
}

export function openSettings() {
  const picker = document.getElementById('settings-add-color');
  if (picker) {
    const current = picker.dataset.selected || COLORS[0].id;
    picker.innerHTML = colorPickerHTML(current);
    picker.dataset.selected = current;
    wireColorPicker(picker);
  }
  renderBlockTypesList();
  renderCalendarSettings();
  document.getElementById('settings-overlay').classList.add('open');
}

export function closeSettings() {
  document.getElementById('settings-overlay').classList.remove('open');
}

let _editingTypeId = null;

const ICONS = {
  edit:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  x:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  plus:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
};
function iconSVG(name) { return ICONS[name] || ''; }

function colorPickerHTML(selectedId) {
  return COLORS.map(c => {
    const sel = c.id === selectedId ? ' is-selected' : '';
    return `<button type="button" class="color-chip b-${c.id}${sel}" data-color="${c.id}" title="${c.id}" aria-label="${c.id}"></button>`;
  }).join('');
}

function wireColorPicker(rootEl) {
  if (!rootEl || rootEl._wired) return;
  rootEl._wired = true;
  rootEl.addEventListener('click', e => {
    const btn = e.target.closest('.color-chip');
    if (!btn) return;
    e.preventDefault();
    const color = btn.dataset.color;
    rootEl.dataset.selected = color;
    rootEl.querySelectorAll('.color-chip').forEach(c => c.classList.toggle('is-selected', c === btn));
  });
}

export function renderBlockTypesList() {
  const list = document.getElementById('settings-types-list');
  if (!list) return;
  if (state.blockTypes.length === 0) {
    list.innerHTML = '<div style="color:var(--text-3);font-size:0.85rem;padding:8px 0;">Cap tipus definit.</div>';
    return;
  }
  list.innerHTML = state.blockTypes.map(t => {
    const colorId = COLORS.find(c => c.id === t.color) ? t.color : 'sky';
    if (_editingTypeId === t.id) {
      return `<div class="settings-type-row editing" data-id="${esc(t.id)}">
        <input class="form-input settings-edit-emoji" type="text" maxlength="2" value="${esc(t.emoji || '')}">
        <input class="form-input settings-edit-label" type="text" value="${esc(t.label)}">
        <input class="form-input settings-edit-description" type="text" placeholder="Descripció (opcional)" value="${esc(t.description || '')}">
        <div class="settings-edit-actions">
          <button class="settings-icon-btn is-accent settings-type-save" data-id="${esc(t.id)}" title="Desar">${iconSVG('check')}</button>
          <button class="settings-icon-btn settings-type-cancel" data-id="${esc(t.id)}" title="Cancel·lar">${iconSVG('x')}</button>
        </div>
        <div class="color-picker settings-edit-color" data-selected="${esc(colorId)}">${colorPickerHTML(colorId)}</div>
      </div>`;
    }
    return `<div class="settings-type-row b-${colorId}" data-id="${esc(t.id)}">
      <span class="settings-type-emoji">${esc(t.emoji || '')}</span>
      <span class="settings-type-label">${esc(t.label)}</span>
      <button class="settings-icon-btn settings-type-edit" data-id="${esc(t.id)}" title="Editar">${iconSVG('edit')}</button>
      <button class="settings-icon-btn is-danger settings-type-del" data-id="${esc(t.id)}" title="Eliminar">${iconSVG('trash')}</button>
    </div>`;
  }).join('');

  list.querySelectorAll('.color-picker').forEach(wireColorPicker);

  list.querySelectorAll('.settings-type-del').forEach(btn => {
    btn.addEventListener('click', () => deleteBlockTypeUI(btn.dataset.id));
  });
  list.querySelectorAll('.settings-type-edit').forEach(btn => {
    btn.addEventListener('click', () => { _editingTypeId = btn.dataset.id; renderBlockTypesList(); });
  });
  list.querySelectorAll('.settings-type-cancel').forEach(btn => {
    btn.addEventListener('click', () => { _editingTypeId = null; renderBlockTypesList(); });
  });
  list.querySelectorAll('.settings-type-save').forEach(btn => {
    btn.addEventListener('click', () => saveBlockTypeEdit(btn.dataset.id));
  });
}

async function saveBlockTypeEdit(id) {
  const row = document.querySelector(`.settings-type-row.editing[data-id="${id}"]`);
  if (!row) return;
  const emoji = row.querySelector('.settings-edit-emoji').value.trim();
  const label = row.querySelector('.settings-edit-label').value.trim();
  const description = row.querySelector('.settings-edit-description').value.trim();
  const color = row.querySelector('.settings-edit-color').dataset.selected || 'sky';
  if (!label) { toast('El nom no pot estar buit'); return; }

  const resp = await apiFetch(`/api/block-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ label, emoji, color, description: description || null }),
  });
  if (!resp?.ok) { toast('Error guardant el tipus'); return; }
  const updated = await resp.json();
  const i = state.blockTypes.findIndex(t => t.id === id);
  if (i !== -1) state.blockTypes[i] = updated;

  _editingTypeId = null;
  renderBlockTypesList();
  fillTipusButtons(state.pickedTipus);
  renderAllBlocksExternal();
  updateWeekSummaryExternal();
  renderSidebarExternal();
}

export async function addBlockTypeFromForm() {
  const emoji = document.getElementById('settings-add-emoji').value.trim();
  const label = document.getElementById('settings-add-label').value.trim();
  const color = document.getElementById('settings-add-color').dataset.selected || 'sky';
  const description = document.getElementById('settings-add-description').value.trim();
  if (!label) return;

  const resp = await apiFetch('/api/block-types', {
    method: 'POST',
    body: JSON.stringify({ label, emoji, color, description: description || null }),
  });
  if (!resp?.ok) return;
  const newType = await resp.json();
  state.blockTypes.push(newType);

  document.getElementById('settings-add-emoji').value = '';
  document.getElementById('settings-add-label').value = '';
  document.getElementById('settings-add-description').value = '';
  renderBlockTypesList();
  fillTipusButtons(state.pickedTipus);
}

async function deleteBlockTypeUI(id) {
  if (!confirm('Eliminar aquest tipus de bloc?')) return;
  await apiFetch(`/api/block-types/${id}`, { method: 'DELETE' });
  state.blockTypes = state.blockTypes.filter(t => t.id !== id);
  renderBlockTypesList();
  fillTipusButtons(state.pickedTipus);
  updateWeekSummaryExternal();
}

// ══════════════════════════════════════════════════════════
// GCAL EVENT MODAL
// ══════════════════════════════════════════════════════════
export function openGCalEventModal(ev) {
  state.editingGCalEvent = ev;
  state.pickedGCalColorId = ev.colorId || null;

  document.getElementById('gf-title').value = ev.title || '';
  document.getElementById('gf-description').value = ev.description || '';

  const mondayStr = weekKey(state.currentMonday);
  const [y, mo, d] = mondayStr.split('-').map(Number);
  const epoch = Date.UTC(y, mo - 1, d + ev.day);
  const dateStr = new Date(epoch).toISOString().slice(0, 10);
  const toLocal = slot => {
    const m = START_H * 60 + slot * 15;
    return `${dateStr}T${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  };
  document.getElementById('gf-start').value = toLocal(ev.startSlot);
  document.getElementById('gf-end').value = toLocal(ev.startSlot + (ev.dur || 0));

  const colorRow = document.getElementById('gf-colors');
  colorRow.innerHTML = Object.entries(GCAL_COLORS).map(([id, hex]) =>
    `<button class="color-swatch${state.pickedGCalColorId === id ? ' active' : ''}"
             style="background:${hex}" data-gcolorid="${id}"></button>`
  ).join('');
  colorRow.querySelectorAll('.color-swatch').forEach(btn => {
    btn.addEventListener('click', () => _pickGCalColor(btn.dataset.gcolorid));
  });

  try {
    const gcalLink = `https://calendar.google.com/calendar/r/eventedit?eid=${btoa(ev.id).replace(/=/g, '')}`;
    document.getElementById('gf-open-link').href = gcalLink;
    document.getElementById('gf-open-link').style.display = '';
  } catch {
    document.getElementById('gf-open-link').style.display = 'none';
  }

  document.getElementById('gcal-modal-overlay').classList.add('open');
  document.getElementById('gf-title').focus();
}

function _pickGCalColor(id) {
  state.pickedGCalColorId = id;
  document.querySelectorAll('#gf-colors .color-swatch').forEach(el => {
    el.classList.toggle('active', el.dataset.gcolorid === id);
  });
}

export function closeGCalModal() {
  document.getElementById('gcal-modal-overlay').classList.remove('open');
  state.editingGCalEvent = null;
  state.pickedGCalColorId = null;
}

export async function saveGCalModal() {
  const ev = state.editingGCalEvent;
  if (!ev) return;
  const title = document.getElementById('gf-title').value.trim();
  const startIso = document.getElementById('gf-start').value;
  const endIso = document.getElementById('gf-end').value;
  const description = document.getElementById('gf-description').value.trim();
  if (!title || !startIso || !endIso) { toast('Títol i hora requerits'); return; }

  const calIdEnc = encodeURIComponent(ev.calendarId);
  const resp = await apiFetch(`/api/calendar/external/${calIdEnc}/${ev.id}`, {
    method: 'PUT',
    body: JSON.stringify({ title, startIso, endIso, description, colorId: state.pickedGCalColorId }),
  });
  if (resp?.ok) {
    toast('Event actualitzat');
    closeGCalModal();
    await loadCalendarEventsAPI(weekKey(state.currentMonday));
    renderCalendarEventsExternal();
  } else {
    toast('Error en guardar');
  }
}

// ══════════════════════════════════════════════════════════
// Cross-module call bridges (set by main.js at init time)
// ══════════════════════════════════════════════════════════
let rerenderBlockExternal      = () => {};
let renderBlockExternal        = () => {};
let renderAllBlocksExternal    = () => {};
let updateWeekSummaryExternal  = () => {};
let handleDeleteBlockExternal  = () => {};
let renderSidebarExternal      = () => {};
let renderCalendarEventsExternal = () => {};

export function registerModalCallbacks({
  rerenderBlock, renderBlock, renderAllBlocks, updateWeekSummary,
  handleDeleteBlock, renderSidebar, renderCalendarEvents,
}) {
  rerenderBlockExternal       = rerenderBlock;
  renderBlockExternal         = renderBlock;
  renderAllBlocksExternal     = renderAllBlocks;
  updateWeekSummaryExternal   = updateWeekSummary;
  handleDeleteBlockExternal   = handleDeleteBlock;
  renderSidebarExternal       = renderSidebar;
  renderCalendarEventsExternal = renderCalendarEvents;
}
